import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';

/**
 * Panel administrativo de la integración MyTime (teletrabajo).
 * Permite forzar sincronización manual y ver:
 *   - Estado de conexión (API key configurada o no)
 *   - Última sincronización (cuándo, qué rango, totales)
 *   - Lista de empleados no matcheados (para que RRHH los revise)
 */
export function MyTimePanel() {
  const { apiFetch, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [status, setStatus]       = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [cfg, setCfg]             = useState(null);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [days, setDays]           = useState(7);
  const [reloadTick, setTick]     = useState(0);
  const [linkingRow, setLinkingRow] = useState(null); // index of row being linked
  const [linkValue, setLinkValue]   = useState('');
  const [linkBusy, setLinkBusy]     = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sr, ur, cr] = await Promise.all([
          apiFetch('/api/integrations/mytime/status'),
          apiFetch('/api/integrations/mytime/unmatched?limit=200'),
          apiFetch('/api/config'),
        ]);
        if (cancelled) return;
        if (sr.ok) setStatus(await sr.json());
        if (ur.ok) setUnmatched((await ur.json()).data || []);
        if (cr.ok) setCfg(await cr.json());
      } catch {
        if (!cancelled) toast.error('Error cargando MyTime');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch, reloadTick]);

  const handleSync = async () => {
    if (!window.confirm(`Forzar sincronización MyTime de los últimos ${days} días?`)) return;
    setSyncing(true);
    try {
      const res = await apiFetch(`/api/integrations/mytime/sync?days_back=${days}`, { method: 'POST' });
      if (res.ok) {
        toast.success('Sincronización lanzada en background');
        // Refresh after a few seconds
        setTimeout(() => { setLoading(true); setTick(t => t + 1); }, 6000);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Error al lanzar sincronización');
      }
    } catch {
      toast.error('Error de conexión');
    }
    setSyncing(false);
  };

  const openLink = (i, prefill) => {
    setLinkingRow(i);
    setLinkValue(prefill || '');
  };

  const confirmLink = async (row) => {
    if (!linkValue.trim()) { toast.error('Indica un número de personal'); return; }
    setLinkBusy(true);
    try {
      const res = await apiFetch('/api/integrations/mytime/link-employee', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: row.email, numero_personal: linkValue.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Vinculado a Nº ${linkValue.trim()} — ${d.sessions_relinked} sesión(es) reprocesada(s) al momento`);
        setLinkingRow(null);
        setLinkValue('');
        setTick(t => t + 1);
      } else {
        toast.error(d.detail || 'Error al vincular');
      }
    } catch {
      toast.error('Error de conexión');
    }
    setLinkBusy(false);
  };

  const last = status?.last_sync;
  const configured = status?.configured;
  const everRun    = status?.ever_run;
  const syncHour   = cfg?.mytime_sync_hour ?? 3;
  const syncWindow = cfg?.mytime_sync_window_days ?? 7;
  const errorDetails = last?.error_details || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}
    >
      <div>
        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          MyTime · Integración Teletrabajo
          <span style={{ background: '#451a03', color: '#fbbf24', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>NUEVO</span>
        </h3>
        <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
          Cada noche a las {String(syncHour).padStart(2, '0')}:00 (Europe/Madrid) descargamos los últimos {syncWindow} días
          de fichajes de teletrabajo desde MyTime y los integramos en la tabla de Fichajes, marcados con badge
          «Teletrabajo». Solo se importan días sin fichaje presencial.
        </p>
      </div>

      {/* Connection status */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
        <Pill ok={configured} okText="Conectada" badText="API key no configurada" />
        <Pill ok={everRun} okText={`Última sincro: ${last?.started_at?.slice(0, 16).replace('T', ' ')} (Madrid)`} badText="Sin ejecuciones todavía" />
      </div>

      {/* Last sync stats */}
      {last && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
          <Stat label="Descargadas"     value={last.downloaded}      color="#94a3b8" />
          <Stat label="Insertadas"      value={last.inserted}        color="#22c55e" />
          <Stat label="Actualizadas"    value={last.updated}         color="#3b82f6" />
          <Stat label="Espejo /fichajes" value={last.kpi_mirrored}   color="#a855f7" />
          <Stat label="Saltadas (pres.)" value={last.kpi_skipped_already_presential} color="#94a3b8" />
          <Stat label="No matcheadas"   value={last.unmatched}       color="#f59e0b" />
          <Stat label="Errores"         value={last.errors}          color="#ef4444" />
        </div>
      )}

      {/* Error detail (brief P1#1: "mostrar detalle de los errores") */}
      {errorDetails.length > 0 && (
        <div>
          <button
            data-testid="mytime-toggle-errors"
            onClick={() => setShowErrors(v => !v)}
            style={{ background: 'transparent', border: 'none', color: '#f87171', fontSize: '12px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            {showErrors ? 'Ocultar' : 'Ver'} detalle de los {errorDetails.length} errores del último sync
          </button>
          {showErrors && (
            <div style={{ background: '#0f172a', border: '1px solid #7f1d1d', borderRadius: '8px', padding: '10px 14px', marginTop: '8px', maxHeight: '180px', overflow: 'auto' }}>
              {errorDetails.map((e, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#fca5a5', marginBottom: '4px' }}>
                  <strong style={{ color: '#f1f5f9' }}>{e.item}:</strong> {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manual sync */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid #334155', paddingTop: '16px' }}>
          <div>
            <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Ventana en días</label>
            <Input
              data-testid="mytime-days"
              type="number"
              min={1}
              max={30}
              value={days}
              onChange={e => setDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
              style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', width: '120px', height: '40px' }}
            />
          </div>
          <Button
            data-testid="mytime-sync-now"
            onClick={handleSync}
            disabled={syncing || !configured}
            style={{ background: '#3b82f6', color: '#fff', height: '40px', padding: '0 18px', fontWeight: 600 }}
          >
            {syncing ? 'Iniciando…' : 'Forzar sincronización ahora'}
          </Button>
          {!configured && (
            <span style={{ color: '#f87171', fontSize: '12px', alignSelf: 'center' }}>
              Configura VALORIZA_MYTIME_API_KEY en backend/.env y reinicia para activar.
            </span>
          )}
        </div>
      )}

      {/* Unmatched employees table */}
      {unmatched.length > 0 && (
        <div>
          <h4 style={{ color: '#fbbf24', fontSize: '13px', fontWeight: 600, margin: '8px 0 8px' }}>
            Empleados no encontrados en el maestro local ({unmatched.length})
          </h4>
          <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 10px' }}>
            Estos empleados tienen fichajes en MyTime pero no aparecen en SmartClockIn.
            Si ya existen con otro Nº de personal, vincúlalos manualmente — se reprocesan sus sesiones al instante.
          </p>
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', maxHeight: '460px', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#1e293b', position: 'sticky', top: 0 }}>
                  <th style={th}>Nº personal</th>
                  <th style={th}>DNI</th>
                  <th style={th}>Email</th>
                  <th style={th}>Nombre</th>
                  <th style={{ ...th, textAlign: 'right' }}>Sesiones</th>
                  <th style={th}>Última fecha</th>
                  {isAdmin && <th style={th}>Acción</th>}
                </tr>
              </thead>
              <tbody>
                {unmatched.map((u, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #1e293b' }}>
                    <td style={{ ...td, fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>{u.numero_personal || '-'}</td>
                    <td style={{ ...td, fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>{u.dni || '-'}</td>
                    <td style={td}>{u.email || '-'}</td>
                    <td style={td}>{[u.first_name, u.last_name].filter(Boolean).join(' ')}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'IBM Plex Mono' }}>{u.sessions}</td>
                    <td style={{ ...td, fontFamily: 'IBM Plex Mono', color: '#94a3b8' }}>{u.last_date}</td>
                    {isAdmin && (
                      <td style={td}>
                        {!u.email ? (
                          <span style={{ color: '#475569', fontSize: '11px' }}>Sin email, no se puede vincular</span>
                        ) : linkingRow === i ? (
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <Input
                              data-testid={`mytime-link-input-${i}`}
                              value={linkValue}
                              onChange={e => setLinkValue(e.target.value)}
                              placeholder="Nº personal"
                              style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', width: '110px', height: '30px', fontSize: '12px' }}
                            />
                            <Button
                              data-testid={`mytime-link-confirm-${i}`}
                              onClick={() => confirmLink(u)}
                              disabled={linkBusy}
                              style={{ background: '#22c55e', color: '#052e16', height: '30px', padding: '0 10px', fontSize: '12px', fontWeight: 600 }}
                            >
                              {linkBusy ? '...' : 'OK'}
                            </Button>
                            <button
                              onClick={() => setLinkingRow(null)}
                              style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '16px', cursor: 'pointer' }}
                            >×</button>
                          </div>
                        ) : u.suggested_employee ? (
                          <Button
                            data-testid={`mytime-link-suggestion-${i}`}
                            onClick={() => openLink(i, u.suggested_employee.numero_personal)}
                            style={{ background: '#1e3a8a', color: '#bfdbfe', height: '28px', padding: '0 10px', fontSize: '11px', fontWeight: 500 }}
                          >
                            ¿Es Nº {u.suggested_employee.numero_personal}? Vincular
                          </Button>
                        ) : (
                          <Button
                            data-testid={`mytime-link-open-${i}`}
                            onClick={() => openLink(i, '')}
                            style={{ background: '#334155', color: '#e2e8f0', height: '28px', padding: '0 10px', fontSize: '11px', fontWeight: 500 }}
                          >
                            Vincular con empleado
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && <div style={{ color: '#64748b' }}>Cargando…</div>}
    </motion.div>
  );
}

const Pill = ({ ok, okText, badText }) => (
  <div
    data-testid={ok ? 'mytime-pill-ok' : 'mytime-pill-bad'}
    style={{
      background: ok ? '#14532d' : '#7f1d1d',
      border: `1px solid ${ok ? '#22c55e' : '#dc2626'}`,
      color: ok ? '#d1fae5' : '#fecaca',
      padding: '6px 12px', borderRadius: '999px',
      fontSize: '12px', fontWeight: 500,
      display: 'inline-flex', alignItems: 'center', gap: '6px',
    }}
  >
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: ok ? '#22c55e' : '#ef4444' }} />
    {ok ? okText : badText}
  </div>
);

const Stat = ({ label, value, color }) => (
  <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px 14px' }}>
    <div style={{ color: '#64748b', fontSize: '11px' }}>{label}</div>
    <div style={{ color, fontSize: '20px', fontWeight: 600, fontFamily: 'IBM Plex Mono' }}>
      {(value ?? 0).toLocaleString('es-ES')}
    </div>
  </div>
);

const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '8px 12px', color: '#f1f5f9' };
